import { useState, type ReactNode, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DateInput, validateDateSubmission } from '../../../../shared/components/DateInput.js';
import { createCompany, type CreateCompanyInput } from '../../api/companyApi.js';
import { PAN_PATTERN as PAN_RE } from '../../../../shared/contracts/backend-enums.js';

const CODE_RE = /^[A-Z0-9_]{2,20}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[0-9\-\s]{7,20}$/;
const PIN_RE = /^[0-9]{4,10}$/;

interface FieldDef {
  readonly key: keyof CreateCompanyInput;
  readonly label: string;
  readonly required?: boolean;
  readonly type?: 'text' | 'email' | 'date' | 'textarea';
}

const FIELDS: readonly FieldDef[] = [
  { key: 'companyCode', label: 'Code', required: true },
  { key: 'companyName', label: 'Name', required: true },
  { key: 'legalName', label: 'Legal name', required: true },
  { key: 'panNumber', label: 'PAN number' },
  { key: 'country', label: 'Country' },
  { key: 'state', label: 'State' },
  { key: 'district', label: 'District' },
  { key: 'pincode', label: 'Pincode' },
  { key: 'address', label: 'Address', type: 'textarea' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'website', label: 'Website' },
  { key: 'effectiveFrom', label: 'Effective from', type: 'date' },
  { key: 'effectiveTo', label: 'Effective to', type: 'date' },
];

export function CompanyCreatePage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState<Record<string, string>>({ country: 'IN' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!CODE_RE.test(form.companyCode ?? ''))
      e.companyCode = t('companies.err.code');
    if (!form.companyName || form.companyName.trim().length < 2)
      e.companyName = t('companies.err.nameRequired');
    if (!form.legalName || form.legalName.trim().length < 2)
      e.legalName = t('companies.err.legalNameRequired');
    if (form.panNumber && !PAN_RE.test(form.panNumber))
      e.panNumber = t('companies.err.pan');
    if (form.email && !EMAIL_RE.test(form.email))
      e.email = t('companies.err.email');
    if (form.phone && !PHONE_RE.test(form.phone))
      e.phone = t('companies.err.phone');
    if (form.pincode && !PIN_RE.test(form.pincode))
      e.pincode = t('companies.err.pincode');
    if (
      form.effectiveFrom &&
      form.effectiveTo &&
      new Date(form.effectiveTo) <= new Date(form.effectiveFrom)
    )
      e.effectiveTo = t('companies.err.effectiveTo');
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  const onSubmit = async (ev: FormEvent) => {
    ev.preventDefault();
    setError(null);
    if (!validate()) return;
    const draft: Record<string, unknown> = {
      companyCode: form.companyCode,
      companyName: form.companyName,
      legalName: form.legalName,
    };
    for (const k of [
      'panNumber', 'country', 'state', 'district',
      'pincode', 'address', 'phone', 'email', 'website',
    ] as const) {
      if (form[k]) draft[k] = form[k];
    }
    if (form.effectiveFrom)
      draft.effectiveFrom = new Date(form.effectiveFrom).toISOString();
    if (form.effectiveTo)
      draft.effectiveTo = new Date(form.effectiveTo).toISOString();
    const body: CreateCompanyInput = draft as unknown as CreateCompanyInput;
    setSaving(true);
    try {
      await createCompany(body, token);
      nav('/admin/companies');
    } catch (err) {
      setError((err as Error).message);
      setSaving(false);
    }
  };

  return (
    <main aria-label="company-create">
      <h1>{t('companies.newTitle')}</h1>
      {error && <p role="alert">{error}</p>}
      <form onSubmit={onSubmit} aria-label="company-form" noValidate onSubmitCapture={validateDateSubmission}>
        {FIELDS.map((f) => {
          const id = `cf-${String(f.key)}`;
          const val = form[f.key as string] ?? '';
          const err = errors[f.key as string];
          return (
            <div key={String(f.key)} className="company-field">
              <label htmlFor={id}>
                {t(`companies.field.${String(f.key)}`)}
                {f.required ? ' *' : ''}
              </label>
              {f.type === 'textarea' ? (
                <textarea
                  id={id}
                  value={val}
                  onChange={(e) => set(f.key as string, e.target.value)}
                  data-testid={`field-${String(f.key)}`}
                />
              ) : f.type === 'date' ? (
                <DateInput
                  id={id}
                  value={val}
                  onValueChange={(nextValue) => set(f.key as string, nextValue)}
                  data-testid={`field-${String(f.key)}`}
                />
              ) : (
                <input
                  id={id}
                  type={f.type === 'email' ? 'email' : 'text'}
                  value={val}
                  onChange={(e) => set(f.key as string, e.target.value)}
                  data-testid={`field-${String(f.key)}`}
                />
              )}
              {err ? (
                <span className="field-error" data-testid={`err-${String(f.key)}`}>
                  {err}
                </span>
              ) : null}
            </div>
          );
        })}
        <button type="submit" disabled={saving} data-testid="submit-create">
          {saving ? t('companies.saving') : t('companies.create')}
        </button>
      </form>
    </main>
  );
}
